import React, { useState, useEffect } from 'react';
import { useStudentAuth } from '../../context/StudentAuthContext';
import { Link, useSearchParams } from 'react-router-dom';
import toast from 'react-hot-toast';
import { commonAPI } from '../../services/api'; // adjust path to your commonAPI

const StudentLogin = () => {
  const [searchParams] = useSearchParams();
  const [studentReg, setStudentReg] = useState('');
  const [password, setPassword] = useState('');
  const [regCode, setRegCode] = useState('');
  const [loading, setLoading] = useState(false);
  const [schoolInfo, setSchoolInfo] = useState(null); // { name, motto, logo_base64, ... }
  const [loadingSchool, setLoadingSchool] = useState(false);
  const { studentLogin } = useStudentAuth();

  // Read reg_code from URL and fetch school config
  useEffect(() => {
    const urlRegCode = searchParams.get('reg_code');
    if (urlRegCode) {
      setRegCode(urlRegCode); // pre-fill the input
      setLoadingSchool(true);
      commonAPI.getSchoolConfig(urlRegCode)
        .then(response => {
          setSchoolInfo(response.data);
        })
        .catch(error => {
          console.error('School not found or error:', error);
          setSchoolInfo(null); // fallback to default branding
        })
        .finally(() => setLoadingSchool(false));
    } else {
      setSchoolInfo(null);
    }
  }, [searchParams]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    await studentLogin(studentReg, password, regCode);
    setLoading(false);
  };

  // Determine display branding
  const displayTitle = schoolInfo?.name || 'Student Portal';
  const displayMotto = schoolInfo?.motto;
  const displayLogo = schoolInfo?.logo_base64;

  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
      <div className="max-w-md w-full mx-4 space-y-8 p-6 md:p-8 bg-white rounded-2xl shadow-xl">
        {/* Branding / Welcome Area */}
        <div className="text-center">
          {displayLogo ? (
            <div className="flex justify-center mb-4">
              <img 
                src={displayLogo} 
                alt={displayTitle} 
                className="h-16 w-auto object-contain"
              />
            </div>
          ) : (
            <div className="flex justify-center mb-6">
              <div className="w-14 h-14 md:w-16 md:h-16 bg-blue-100 rounded-full flex items-center justify-center">
                <i className="fas fa-graduation-cap text-2xl md:text-3xl text-blue-600"></i>
              </div>
            </div>
          )}
          <h2 className="text-2xl md:text-3xl font-bold text-gray-900">{displayTitle}</h2>
          {displayMotto && (
            <p className="mt-1 text-sm italic text-gray-500">“{displayMotto}”</p>
          )}
          <p className="mt-2 text-sm md:text-base text-gray-600">Sign in to access your academic records</p>
          {loadingSchool && <p className="text-xs text-gray-400 mt-2">Loading school info...</p>}
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          <div className="space-y-4">
            {/* School Registration Code Field - pre-filled from URL if present */}
            <div>
              <label htmlFor="regCode" className="block text-sm font-medium text-gray-700 mb-1">
                School Registration Code
              </label>
              <div className="relative">
                <span className="absolute left-0 inset-y-0 flex items-center w-12 justify-center text-gray-400 pointer-events-none">
                  <i className="fas fa-qrcode"></i>
                </span>
                <input
                  id="regCode"
                  name="regCode"
                  type="text"
                  required
                  value={regCode}
                  onChange={(e) => setRegCode(e.target.value)}
                  className="w-full pl-12 pr-3 py-3 md:py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base transition-all duration-200"
                  placeholder="Enter school code"
                />
              </div>
              <p className="text-xs text-gray-500 mt-1">
                {schoolInfo 
                  ? "✓ Valid school detected. You may change the code to log into another school." 
                  : "Enter your school's registration code"}
              </p>
            </div>

            {/* Student Registration Number Field */}
            <div>
              <label htmlFor="studentReg" className="block text-sm font-medium text-gray-700 mb-1">
                Student Registration Number
              </label>
              <div className="relative">
                <span className="absolute left-0 inset-y-0 flex items-center w-12 justify-center text-gray-400 pointer-events-none">
                  <i className="fas fa-id-card"></i>
                </span>
                <input
                  id="studentReg"
                  name="studentReg"
                  type="text"
                  required
                  value={studentReg}
                  onChange={(e) => setStudentReg(e.target.value)}
                  className="w-full pl-12 pr-3 py-3 md:py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base transition-all duration-200"
                  placeholder="Enter your registration number"
                />
              </div>
            </div>

            {/* Password Field */}
            <div>
              <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
                Password
              </label>
              <div className="relative">
                <span className="absolute left-0 inset-y-0 flex items-center w-12 justify-center text-gray-400 pointer-events-none">
                  <i className="fas fa-lock"></i>
                </span>
                <input
                  id="password"
                  name="password"
                  type="password"
                  required
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  className="w-full pl-12 pr-3 py-3 md:py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base transition-all duration-200"
                  placeholder="Enter your password"
                />
              </div>
              <p className="mt-2 text-xs text-gray-500">
                Default password: <span className="font-mono">12345678</span>
              </p>
            </div>
          </div>

          <div>
            <button
              type="submit"
              disabled={loading}
              className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            >
              {loading ? (
                <>
                  <i className="fas fa-spinner fa-spin mr-2"></i>
                  Signing in...
                </>
              ) : (
                <>
                  <span className="absolute left-0 inset-y-0 flex items-center pl-3">
                    <i className="fas fa-sign-in-alt text-sm md:text-base"></i>
                  </span>
                  Sign in to Student Portal
                </>
              )}
            </button>
          </div>

          <div className="text-center text-gray-600">
            <div className="mb-4">
              <p className="text-xs text-gray-500">
                <Link to="/student_login/forgot" className="text-blue-600 hover:text-blue-700 hover:underline font-medium">
                  Forgot your password?
                </Link>
              </p>
            </div>

            {/* Info Panel - unchanged */}
            <div className="pt-4 border-t border-gray-200 space-y-3">
              <div className="bg-blue-50 rounded-lg p-3 text-left text-xs text-blue-800 space-y-2">
                <p className="font-semibold">📘 Student Portal Guide</p>
                <ul className="list-disc list-inside space-y-1">
                  <li>Use your registration number and school code to log in</li>
                  <li>View your grades, assessment breakdown, and performance history</li>
                  <li>Access assigned quizzes and track your progress</li>
                  <li>Download result reports (if enabled by your school)</li>
                </ul>
              </div>

              <p className="text-xs text-gray-500">
                Need help?{' '}
                <a
                  href="https://cbthost.com/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
                >
                  Contact support
                </a>
              </p>
              <p className="text-xs text-gray-500">
                Powered and hosted by{' '}
                <a
                  href="https://cbthost.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
                >
                  cbthost.com
                </a>
              </p>
            </div>
          </div>
        </form>
      </div>
    </div>
  );
};

export default StudentLogin;

// import React, { useState } from 'react';
// import { useStudentAuth } from '../../context/StudentAuthContext';
// import { Link } from 'react-router-dom';
// import toast from 'react-hot-toast';

// const StudentLogin = () => {
//   const [studentReg, setStudentReg] = useState('');
//   const [password, setPassword] = useState('');
//   const [regCode, setRegCode] = useState('');
//   const [loading, setLoading] = useState(false);
//   const { studentLogin } = useStudentAuth();

//   const handleSubmit = async (e) => {
//     e.preventDefault();
//     setLoading(true);
//     await studentLogin(studentReg, password, regCode);
//     setLoading(false);
//   };

//   return (
//     <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-blue-50 to-indigo-50">
//       <div className="max-w-md w-full mx-4 space-y-8 p-6 md:p-8 bg-white rounded-2xl shadow-xl">
//         <div className="text-center">
//           <div className="flex justify-center mb-6">
//             <div className="w-14 h-14 md:w-16 md:h-16 bg-blue-100 rounded-full flex items-center justify-center">
//               <i className="fas fa-graduation-cap text-2xl md:text-3xl text-blue-600"></i>
//             </div>
//           </div>
//           <h2 className="text-2xl md:text-3xl font-bold text-gray-900">Student Portal</h2>
//           <p className="mt-2 text-sm md:text-base text-gray-600">Sign in to access your academic records</p>
//         </div>

//         <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
//           <div className="space-y-4">
//             {/* School Registration Code Field */}
//             <div>
//               <label htmlFor="regCode" className="block text-sm font-medium text-gray-700 mb-1">
//                 School Registration Code
//               </label>
//               <div className="relative">
//                 <span className="absolute left-0 inset-y-0 flex items-center w-12 justify-center text-gray-400 pointer-events-none">
//                   <i className="fas fa-qrcode"></i>
//                 </span>
//                 <input
//                   id="regCode"
//                   name="regCode"
//                   type="text"
//                   required
//                   value={regCode}
//                   onChange={(e) => setRegCode(e.target.value)}
//                   className="w-full pl-12 pr-3 py-3 md:py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base transition-all duration-200"
//                   placeholder="Enter school code"
//                 />
//               </div>
//             </div>

//             {/* Student Registration Number Field */}
//             <div>
//               <label htmlFor="studentReg" className="block text-sm font-medium text-gray-700 mb-1">
//                 Student Registration Number
//               </label>
//               <div className="relative">
//                 <span className="absolute left-0 inset-y-0 flex items-center w-12 justify-center text-gray-400 pointer-events-none">
//                   <i className="fas fa-id-card"></i>
//                 </span>
//                 <input
//                   id="studentReg"
//                   name="studentReg"
//                   type="text"
//                   required
//                   value={studentReg}
//                   onChange={(e) => setStudentReg(e.target.value)}
//                   className="w-full pl-12 pr-3 py-3 md:py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base transition-all duration-200"
//                   placeholder="Enter your registration number"
//                 />
//               </div>
//             </div>

//             {/* Password Field */}
//             <div>
//               <label htmlFor="password" className="block text-sm font-medium text-gray-700 mb-1">
//                 Password
//               </label>
//               <div className="relative">
//                 <span className="absolute left-0 inset-y-0 flex items-center w-12 justify-center text-gray-400 pointer-events-none">
//                   <i className="fas fa-lock"></i>
//                 </span>
//                 <input
//                   id="password"
//                   name="password"
//                   type="password"
//                   required
//                   value={password}
//                   onChange={(e) => setPassword(e.target.value)}
//                   className="w-full pl-12 pr-3 py-3 md:py-3.5 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent text-sm md:text-base transition-all duration-200"
//                   placeholder="Enter your password"
//                 />
//               </div>
//               <p className="mt-2 text-xs text-gray-500">
//                 Default password: <span className="font-mono">12345678</span>
//               </p>
//             </div>
//           </div>

//           <div>
//             <button
//               type="submit"
//               disabled={loading}
//               className="group relative w-full flex justify-center py-3 px-4 border border-transparent text-sm font-medium rounded-lg bg-blue-600 hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
//             >
//               {loading ? (
//                 <>
//                   <i className="fas fa-spinner fa-spin mr-2"></i>
//                   Signing in...
//                 </>
//               ) : (
//                 <>
//                   <span className="absolute left-0 inset-y-0 flex items-center pl-3">
//                     <i className="fas fa-sign-in-alt text-sm md:text-base"></i>
//                   </span>
//                   Sign in to Student Portal
//                 </>
//               )}
//             </button>
//           </div>

//           <div className="text-center text-gray-600">
//             <div className="mb-4">
//               <p className="text-xs text-gray-500">
//                 <Link to="/student_login/forgot" className="text-blue-600 hover:text-blue-700 hover:underline font-medium">
//                   Forgot your password?
//                 </Link>
//               </p>
//             </div>

//             {/* Info Panel */}
//             <div className="pt-4 border-t border-gray-200 space-y-3">
//               <div className="bg-blue-50 rounded-lg p-3 text-left text-xs text-blue-800 space-y-2">
//                 <p className="font-semibold">📘 Student Portal Guide</p>
//                 <ul className="list-disc list-inside space-y-1">
//                   <li>Use your registration number and school code to log in</li>
//                   <li>View your grades, assessment breakdown, and performance history</li>
//                   <li>Access assigned quizzes and track your progress</li>
//                   <li>Download result reports (if enabled by your school)</li>
//                 </ul>
//               </div>

//               <p className="text-xs text-gray-500">
//                 Need help?{' '}
//                 <a
//                   href="https://cbthost.com/contact"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
//                 >
//                   Contact support
//                 </a>
//               </p>
//               <p className="text-xs text-gray-500">
//                 Powered and hosted by{' '}
//                 <a
//                   href="https://cbthost.com"
//                   target="_blank"
//                   rel="noopener noreferrer"
//                   className="text-blue-600 hover:text-blue-700 hover:underline font-medium"
//                 >
//                   cbthost.com
//                 </a>
//               </p>
//             </div>
//           </div>
//         </form>
//       </div>
//     </div>
//   );
// };

// export default StudentLogin;